import React, { useState } from 'react';
import { addDays, addYears, format, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import { BookOpenIcon, HistoryIcon, InboxIcon, LightbulbIcon, LockIcon } from 'lucide-react';
import { MyRequestsTable } from '../../components/self/MyRequestsTable';
import { PtoBalanceCard } from '../../components/self/PtoBalanceCard';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { useSelfService } from '../../hooks/useSelfService';
import type { MyTimeOffTab, Navigate, RequestStatus } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { formatDate } from '../../utils/format';
import { describePolicy } from '../../utils/policy';
import { trimNumber } from '../../utils/selfService';
import { historyFor } from '../../utils/timeOff';
interface MyTimeOffProps {
  tab?: MyTimeOffTab;
  onNavigate: Navigate;
  onRequestTimeOff: () => void;
}
const TABS: {
  id: MyTimeOffTab;
  label: string;
}[] = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'requests',
  label: 'Requests'
}, {
  id: 'history',
  label: 'History'
}];
const FILTERS: ('all' | RequestStatus)[] = ['all', 'pending', 'approved', 'denied', 'cancelled'];
const NEW_HIRE_EXPLAINER = 'Accrual starts after your first 30 days. You earn 1 day per month (prorated), up to 12 days in year one. On your 1-year anniversary your balance tops up to 15 days.';
export function MyTimeOff({
  tab = 'overview',
  onNavigate,
  onRequestTimeOff
}: MyTimeOffProps) {
  const {
    me,
    policy,
    manager,
    requests
  } = useSelfService();
  const [filter, setFilter] = useState<'all' | RequestStatus>('all');
  const managerName = manager ? displayName(manager) : 'your manager';
  const history = historyFor(me);
  const hire = parseISO(me.hireDate);
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const explainer = policy?.id === 'newhire' ? NEW_HIRE_EXPLAINER : policy ? describePolicy(policy.form).sentence : 'You are not assigned to a time off policy yet.';
  const milestones = policy?.id === 'newhire' ? [{
    date: hire,
    title: 'Hired',
    text: 'Enrolled in New Hire PTO'
  }, {
    date: addDays(hire, 30),
    title: 'Accrual starts',
    text: 'After your first 30 days'
  }, {
    date: null,
    title: '1 day each month',
    text: 'Prorated, up to 12 days in year one'
  }, {
    date: addYears(hire, 1),
    title: '1-year anniversary',
    text: 'Balance tops up to 15 days'
  }] : [];
  return <div data-id="element-1761" data-mp="3vonoe9jsihk8">
      <PageHeader eyebrow="My time off" title="Time off" description={`Your ${policy?.name ?? 'PTO'} balance, requests and history.`} data-id="element-1762" data-mp="3vpf9gqfrgww9" />

      <div className="mb-6 flex gap-1 overflow-x-auto rounded-2xl bg-white p-1.5 shadow-soft ring-1 ring-slate-900/[0.04] sm:inline-flex" role="tablist" aria-label="My time off sections" data-id="element-1763" data-mp="15z25xyeax6ci">
        {TABS.map(t => {
        const active = t.id === tab;
        return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => onNavigate({
          page: 'my-timeoff',
          tab: t.id
        })} className={`relative flex items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-medium transition ${active ? 'text-white' : 'text-slate-600 hover:text-navy'}`} data-id="element-1764" data-mp="1g38vt46qkgam">
              {active && <motion.span layoutId="my-timeoff-tab" className="absolute inset-0 rounded-xl bg-navy" transition={{
            type: 'spring',
            bounce: 0.15,
            duration: 0.4
          }} data-id="element-1765" data-mp="xbg2t9ro2v2w" />}
              <span className="relative" data-id="element-1766" data-mp="od4e5dad27d3">{t.label}</span>
              {t.id === 'requests' && pendingCount > 0 && <span className="relative flex h-5 min-w-[20px] items-center justify-center rounded-full bg-flame px-1.5 text-[11px] font-semibold text-white" data-id="element-1767" data-mp="2mqnwcpi32xdw">{pendingCount}</span>}
            </button>;
      })}
      </div>

      {tab === 'overview' && <div className="space-y-6" data-id="element-1768" data-mp="2uswv8l8ets9g">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5" data-id="element-1769" data-mp="2urvh27w3kpni">
            <div className="xl:col-span-3" data-id="element-1770" data-mp="2gru6g0q163uu">
              <PtoBalanceCard onRequest={onRequestTimeOff} data-id="element-1771" data-mp="2guykrqx7mfz8" />
            </div>
            <Card title="How you earn PTO" icon={LightbulbIcon} className="xl:col-span-2" data-id="element-1772" data-mp="2ksw7mn3icpzg">
              <p className="text-sm leading-relaxed text-slate-700" data-id="element-1773" data-mp="271hnue1s0krc">{explainer}</p>
              {milestones.length > 0 && <ol className="mt-5 space-y-3.5 border-l-2 border-slate-100 pl-5" data-id="element-1774" data-mp="2bvvkejs6cxxl">
                  {milestones.map(m => <li key={m.title} className="relative" data-id="element-1775" data-mp="223ptzehkmoki">
                      <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-white ring-2 ring-azure" aria-hidden="true" data-id="element-1776" data-mp="221woi2ip2ae3" />
                      <p className="text-sm font-semibold text-navy" data-id="element-1777" data-mp="1t31f6hkcsk6k">
                        {m.title}
                        {m.date && <span className="ml-2 text-xs font-normal text-muted" data-id="element-1778" data-mp="19gpwzqri7yjz">{format(m.date, 'MMM d, yyyy')}</span>}
                      </p>
                      <p className="text-xs text-muted" data-id="element-1779" data-mp="14k9at23v7u9j">{m.text}</p>
                    </li>)}
                </ol>}
            </Card>
          </div>

          {policy && <Card title={`${policy.name} — policy summary`} subtitle="Read-only · questions go to HR" icon={BookOpenIcon} action={<Badge icon={LockIcon} data-id="element-1781" data-mp="1nhafsmxf5udh">Read-only</Badge>} data-id="element-1780" data-mp="1eip1x52w73j1">
              <p className="text-sm leading-relaxed text-slate-600" data-id="element-1782" data-mp="1ngr7zz1hgmd6">{policy.overview}</p>
              <div className="mt-6 grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2" data-id="element-1783" data-mp="224vgka6tnhi8">
                {policy.sections.map(s => <section key={s.id} data-id="element-1784" data-mp="1x96nmppb4yuw">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-azure" data-id="element-1785" data-mp="2b6bsimdabrok">{s.title}</h3>
                    <dl className="mt-2 divide-y divide-slate-100" data-id="element-1786" data-mp="268jr91qr2t7p">
                      {s.rules.map(r => <div key={r.label} className="flex justify-between gap-4 py-2 text-sm" data-id="element-1787" data-mp="13vngprs57vb3">
                          <dt className="text-muted" data-id="element-1788" data-mp="18u8ivwqygp26">{r.label}</dt>
                          <dd className="text-right font-medium text-navy" data-id="element-1789" data-mp="1cvahcbd1sx33">{r.value}</dd>
                        </div>)}
                    </dl>
                  </section>)}
              </div>
              {policy.example && <p className="mt-6 rounded-xl bg-flame/[0.06] px-4 py-3 text-sm text-slate-700 ring-1 ring-inset ring-flame/15" data-id="element-1790" data-mp="1mmy4w16hhtza">
                  <span className="font-semibold text-navy" data-id="element-1791" data-mp="1wjazr2drmdvm">Example:</span> {policy.example.before} → {policy.example.added} → {policy.example.after}. {policy.example.caption}
                </p>}
            </Card>}
        </div>}

      {tab === 'requests' && <Card title="My requests" subtitle={`${requests.length} total`} icon={InboxIcon} bodyClassName="pb-2" action={<div className="flex flex-wrap gap-1" data-id="element-1793" data-mp="3uujrxk4mlujc">
              {FILTERS.map(f => {
        const count = f === 'all' ? requests.length : requests.filter(r => r.status === f).length;
        return <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-full px-3 py-1 text-xs font-medium capitalize ring-1 ring-inset transition ${filter === f ? 'bg-navy text-white ring-navy' : 'text-slate-600 ring-slate-200 hover:ring-slate-300'}`} data-id="element-1794" data-mp="3h1c13uodd32p">
                    {f} · {count}
                  </button>;
      })}
            </div>} data-id="element-1792" data-mp="66c1p2rs6azm">
          <MyRequestsTable requests={filtered} managerName={managerName} data-id="element-1795" data-mp="2td2h8yapupe7" />
        </Card>}

      {tab === 'history' && <Card title="History" subtitle="Accruals, time used, adjustments and running balance" icon={HistoryIcon} bodyClassName="pb-2" data-id="element-1796" data-mp="2oer7enkf8evz">
          <div className="overflow-x-auto" data-id="element-1797" data-mp="wb3pqdjrthwx">
            <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1798" data-mp="w8qytaw1bx7q">
              <thead data-id="element-1799" data-mp="icds8wt1hg81">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-1800" data-mp="icx3juo6p6d3">
                  <th scope="col" className="px-6 py-2.5" data-id="element-1801" data-mp="1azzxsaltuuxj">Date</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-1802" data-mp="1b1uhxmp9yj0k">Description</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-1803" data-mp="1fxjc1aiiz7ph">Type</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-1804" data-mp="11agej3u7cz25">Used (−)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-1805" data-mp="1691aus6c2ylb">Earned (+)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-1806" data-mp="166olvv25rnyr">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1807" data-mp="1yp2dhcwwidpe">
                {history.map(h => {
              const type = h.used ? 'Used' : /Adjustment/i.test(h.description) ? 'Adjustment' : h.earned ? 'Accrual' : 'Info';
              return <tr key={h.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-1808" data-mp="3egeee0dukvl7">
                      <td className="whitespace-nowrap px-6 py-3 text-slate-600" data-id="element-1809" data-mp="3s7jcgy1khftf">{formatDate(h.date)}</td>
                      <td className="px-6 py-3 text-slate-700" data-id="element-1810" data-mp="3o798ts18z1hq">{h.description}</td>
                      <td className="px-6 py-3" data-id="element-1811" data-mp="30hoholuxwxxd">
                        <Badge tone={type === 'Used' ? 'orange' : type === 'Adjustment' ? 'teal' : type === 'Accrual' ? 'blue' : 'grey'} data-id="element-1812" data-mp="2vi20y2fwkzjt">{type}</Badge>
                      </td>
                      <td className="px-6 py-3 text-right tabular-nums text-rose-600" data-id="element-1813" data-mp="39fgxrs8hk9lt">{h.used ? `−${trimNumber(h.used)}` : ''}</td>
                      <td className="px-6 py-3 text-right tabular-nums text-emerald-600" data-id="element-1814" data-mp="oqt5afu90wq5">{h.earned ? `+${trimNumber(h.earned)}` : ''}</td>
                      <td className="px-6 py-3 text-right font-semibold tabular-nums text-navy" data-id="element-1815" data-mp="jrg9v6pbahby">{trimNumber(h.balance)}</td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </Card>}
    </div>;
}